/* RareTCG Phase 5 — progressive mobile footer disclosures.
 * Native <details> keeps every link reachable without JavaScript.
 * This controller only coordinates mobile accordion state and breakpoint/editor lifecycle. */
(() => {
  if (window.__rtcgFooterPhase5) return;
  window.__rtcgFooterPhase5 = true;

  const FOOTER_SELECTOR = '.raretcg-footer';
  const DISCLOSURE_SELECTOR = '[data-rtcg-footer-disclosure]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const controllers = new WeakMap();

  const syncExpanded = (details) => {
    const summary = details.querySelector(':scope > summary');
    if (!summary) return;
    summary.setAttribute('aria-expanded', details.open ? 'true' : 'false');
  };

  const setupFooter = (footer) => {
    if (!(footer instanceof HTMLElement) || controllers.has(footer)) return;

    const disclosures = Array.from(footer.querySelectorAll(DISCLOSURE_SELECTOR));
    if (!disclosures.length) return;

    const media = window.matchMedia(MOBILE_QUERY);
    let lastMobileMode = null;

    const applyMode = () => {
      const isMobile = media.matches;
      const enteringMobile = isMobile && lastMobileMode !== true;

      disclosures.forEach((details) => {
        const summary = details.querySelector(':scope > summary');
        if (!summary) return;

        if (isMobile) {
          if (enteringMobile) details.open = false;
          summary.removeAttribute('tabindex');
        } else {
          details.open = true;
          summary.setAttribute('tabindex', '-1');
        }

        syncExpanded(details);
      });

      footer.dataset.rtcgFooterReady = 'true';
      footer.dataset.rtcgFooterMode = isMobile ? 'mobile' : 'desktop';
      lastMobileMode = isMobile;
    };

    const onToggle = (event) => {
      const details = event.target;
      if (!(details instanceof HTMLDetailsElement) || !details.matches(DISCLOSURE_SELECTOR)) return;

      if (!media.matches) {
        if (!details.open) details.open = true;
        syncExpanded(details);
        return;
      }

      if (details.open) {
        disclosures.forEach((other) => {
          if (other !== details && other.open) other.open = false;
          syncExpanded(other);
        });
      }

      syncExpanded(details);
    };

    const onKeydown = (event) => {
      if (!media.matches || event.key !== 'Escape') return;
      const details = event.target.closest?.(DISCLOSURE_SELECTOR);
      if (!(details instanceof HTMLDetailsElement) || !details.open) return;

      event.preventDefault();
      details.open = false;
      syncExpanded(details);
      details.querySelector(':scope > summary')?.focus({ preventScroll: true });
    };

    const onMediaChange = () => applyMode();

    footer.addEventListener('toggle', onToggle, true);
    footer.addEventListener('keydown', onKeydown);
    media.addEventListener?.('change', onMediaChange);
    applyMode();

    controllers.set(footer, () => {
      footer.removeEventListener('toggle', onToggle, true);
      footer.removeEventListener('keydown', onKeydown);
      media.removeEventListener?.('change', onMediaChange);
      controllers.delete(footer);
    });
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element) && scope !== document) return;
    if (scope.matches?.(FOOTER_SELECTOR)) setupFooter(scope);
    scope.querySelectorAll?.(FOOTER_SELECTOR).forEach(setupFooter);
  };

  const boot = () => initWithin(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    const footer = event.target.matches?.(FOOTER_SELECTOR)
      ? event.target
      : event.target.querySelector?.(FOOTER_SELECTOR);
    controllers.get(footer)?.();
  });
})();